import { supabase } from '../../lib/supabase'
import { validateFile, sanitizeFilename } from '../../lib/validators'
import { getSignedUrls, logRightToRentAccess } from '../../lib/storage'
import { sendInvite } from '../notifications/invites'
import type {
  Prospect,
  ProspectReferencing,
  ProspectReferencingDocument,
  ReferencingProvider,
  ReferencingDocumentType
} from '../../types/prospect'

// ============================================================
// REFERENCING
// ============================================================

export async function getReferencing(applicationId: string): Promise<ProspectReferencing | null> {
  const { data, error } = await supabase
    .from('prospect_referencing')
    .select(`
      *,
      documents:prospect_referencing_documents(*)
    `)
    .eq('application_id', applicationId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

export async function startReferencing(
  applicationId: string,
  provider: ReferencingProvider,
  requiredDocuments?: ReferencingDocumentType[]
): Promise<ProspectReferencing> {
  const defaultDocs: ReferencingDocumentType[] = ['id_passport', 'proof_of_address', 'payslip', 'bank_statement']
  const docsToRequire = requiredDocuments || defaultDocs

  // Check if a pending referencing record already exists
  const { data: existingRef } = await supabase
    .from('prospect_referencing')
    .select('*')
    .eq('application_id', applicationId)
    .single()

  let data: ProspectReferencing

  if (existingRef) {
    // Update existing referencing record to 'in_progress'
    const { data: updatedRef, error: updateError } = await supabase
      .from('prospect_referencing')
      .update({
        provider,
        status: 'in_progress',
        started_at: new Date().toISOString(),
        required_documents: docsToRequire
      })
      .eq('id', existingRef.id)
      .select()
      .single()

    if (updateError) throw updateError
    data = updatedRef
  } else {
    // Create new referencing record (legacy path)
    const { data: newRef, error: insertError } = await supabase
      .from('prospect_referencing')
      .insert({
        application_id: applicationId,
        provider,
        status: 'in_progress',
        started_at: new Date().toISOString(),
        required_documents: docsToRequire
      })
      .select()
      .single()

    if (insertError) throw insertError
    data = newRef
  }

  // Update application status
  const { data: app } = await supabase
    .from('prospect_applications')
    .update({ status: 'referencing' })
    .eq('id', applicationId)
    .select('prospect_id')
    .single()

  // Prospect stays in 'application' status during referencing
  if (app) {
    await supabase
      .from('prospects')
      .update({ status: 'application' })
      .eq('id', app.prospect_id)

    // A registered prospect is notified by trg_referencing_change
    // (migration 391), which reads the address from the application's own
    // property rather than from an arbitrary first property interest. An
    // unregistered one still needs their email.
    try {
      const { data: prospect } = await supabase
        .from('prospects')
        .select('user_id')
        .eq('id', app.prospect_id)
        .single()

      if (!prospect?.user_id) {
        // The server reads the address off the referencing row's own
        // application, not off an arbitrary first property interest — the
        // same correction migration 391 made for the registered half.
        sendInvite('referencing_started', data.id).catch(() => {})
      }
    } catch {
      // Notification lookup failed - non-blocking
    }
  }

  return data
}

export async function updateReferencing(
  id: string,
  updates: Partial<Omit<ProspectReferencing, 'id' | 'application_id' | 'created_at' | 'updated_at' | 'documents'>>
): Promise<ProspectReferencing> {
  // Auto-set completed_at when status changes to passed or failed
  if ((updates.status === 'passed' || updates.status === 'failed') && !updates.completed_at) {
    updates.completed_at = new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('prospect_referencing')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function getReferencingDocuments(referencingId: string): Promise<ProspectReferencingDocument[]> {
  const { data, error } = await supabase
    .from('prospect_referencing_documents')
    .select('*')
    .eq('referencing_id', referencingId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) throw error
  return await resolveReferencingDocumentUrls(data)
}

/**
 * Resolve file_url in nested referencing documents within a prospect.
 * Walks the prospect.applications[].referencing[].documents[] structure.
 */
export async function resolveProspectDocumentUrls(prospect: Prospect): Promise<Prospect> {
  const applications = (prospect as { applications?: { referencing?: unknown }[] }).applications
  if (!applications) return prospect

  for (const app of applications) {
    const referencings = Array.isArray(app.referencing) ? app.referencing : app.referencing ? [app.referencing] : []
    for (const ref of referencings as { documents?: ProspectReferencingDocument[] }[]) {
      if (ref.documents && ref.documents.length > 0) {
        ref.documents = await resolveReferencingDocumentUrls(ref.documents)
      }
    }
  }
  return prospect
}

// `storage_path` is the stored path; `file_url` is the resolved signed URL and
// is never persisted (migration 480).
async function resolveReferencingDocumentUrls(docs: ProspectReferencingDocument[]): Promise<ProspectReferencingDocument[]> {
  const withPaths = docs.filter(d => d.storage_path)
  if (withPaths.length === 0) return docs

  // Split docs by bucket — Right to Rent docs may be in the dedicated bucket
  const rtrDocs = withPaths.filter(d => d.document_type === 'id_passport' || d.document_type === 'proof_of_address')
  const otherDocs = withPaths.filter(d => d.document_type !== 'id_passport' && d.document_type !== 'proof_of_address')

  const urlMap = new Map<string, string>()

  // Right to Rent docs come from the dedicated bucket only.
  //
  // There used to be a fallback to `tenant-documents` here, for RtR documents
  // uploaded before the dedicated bucket existed. It had to go: it is what
  // hid the fact that `right-to-rent` had NO SELECT policy admitting the
  // prospect who owns the file (fixed in migration 416), and it meant
  // special-category ID documents resolved out of the bucket with the
  // broadest read policy of the twelve. Migration 410 deleted the last 55
  // legacy `referencing/` objects and `prospect_referencing_documents` holds
  // no rows pointing at them, so there is nothing legitimate left to serve.
  if (rtrDocs.length > 0) {
    const rtrPaths = rtrDocs.map(d => d.storage_path)
    const rtrUrls = await getSignedUrls('right-to-rent', rtrPaths)

    rtrDocs.forEach((d, i) => urlMap.set(d.id, rtrUrls[i]))

    // Log access for Right to Rent docs (non-blocking)
    for (const d of rtrDocs) {
      logRightToRentAccess(d.id, 'view').catch(() => {})
    }
  }

  // Resolve other docs from tenant-documents bucket
  if (otherDocs.length > 0) {
    const otherPaths = otherDocs.map(d => d.storage_path)
    const otherUrls = await getSignedUrls('tenant-documents', otherPaths)
    otherDocs.forEach((d, i) => urlMap.set(d.id, otherUrls[i]))
  }

  return docs.map(d =>
    d.storage_path ? { ...d, file_url: urlMap.get(d.id) || '' } : d
  )
}

export async function uploadReferencingDocument(
  referencingId: string,
  file: File,
  documentType: ReferencingDocumentType,
  uploadedBy: string,
  notes?: string
): Promise<ProspectReferencingDocument> {
  // Validate file before upload
  const validation = validateFile(file, { isDocument: true })
  if (!validation.valid) {
    throw new Error(validation.error)
  }

  const sanitizedName = sanitizeFilename(file.name)
  // Right to Rent docs (id_passport, proof_of_address) go to dedicated bucket (UK GDPR Art 9)
  const isRightToRent = documentType === 'id_passport' || documentType === 'proof_of_address'
  const bucket = isRightToRent ? 'right-to-rent' : 'tenant-documents'
  const path = `referencing/${referencingId}/${Date.now()}-${sanitizedName}`
  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(path, file)

  if (uploadError) throw uploadError

  // Store the storage path (not a public URL) — signed URLs are generated at read time
  const { data, error } = await supabase
    .from('prospect_referencing_documents')
    .insert({
      referencing_id: referencingId,
      document_type: documentType,
      document_name: file.name,
      storage_path: path,
      // Dual-written until PR 2b drops the column: retention reaches these
      // rows through `get_soft_deleted_rtr_documents`, which returns file_url.
      file_url: path,
      file_name: file.name,
      file_size: file.size,
      uploaded_by: uploadedBy,
      notes
    })
    .select()
    .single()

  if (error) throw error

  // Log access for Right to Rent documents (UK GDPR Art 9 enhanced logging)
  if (isRightToRent && data?.id) {
    logRightToRentAccess(data.id, 'upload').catch(() => {})
  }

  // The landlord is notified by trg_referencing_document (migration 391).
  // It replaces three reads this prospect session had to make — and the email
  // it could never send, because profiles_select_scoped has no prospect branch
  // so resolveRecipient found no address for the landlord.

  return data
}

export async function deleteReferencingDocument(id: string): Promise<void> {
  const { error } = await supabase.rpc('soft_delete_record', {
    target_table: 'prospect_referencing_documents',
    record_id: id,
  })

  if (error) throw error
}

/**
 * Mark all referencing checks as passed in one action.
 * Useful for quick approval when landlord has verified everything.
 */
export async function markAllChecksPassed(referencingId: string): Promise<ProspectReferencing> {
  const { data, error } = await supabase
    .from('prospect_referencing')
    .update({
      credit_check_status: 'passed',
      employment_check_status: 'passed',
      landlord_ref_status: 'passed',
      right_to_rent_status: 'passed'
    })
    .eq('id', referencingId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Complete the referencing process by marking it as passed.
 * Also updates the application to 'approved' status.
 */
export async function completeReferencing(referencingId: string): Promise<ProspectReferencing> {
  // Update referencing status to passed
  const { data, error } = await supabase
    .from('prospect_referencing')
    .update({
      status: 'passed',
      completed_at: new Date().toISOString()
    })
    .eq('id', referencingId)
    .select('*, application:prospect_applications!prospect_referencing_application_id_fkey(id, prospect_id)')
    .single()

  if (error) throw error

  // Update application status to approved
  const application = (data as { application?: { id: string; prospect_id: string } }).application
  if (application) {
    await supabase
      .from('prospect_applications')
      .update({ status: 'approved', decided_at: new Date().toISOString() })
      .eq('id', application.id)

    // Update prospect status to approved
    await supabase
      .from('prospects')
      .update({ status: 'approved' })
      .eq('id', application.prospect_id)

    // The owner is notified by trg_referencing_change (migration 391), off
    // the status -> 'passed' write above. Note the self-notification guard
    // there: an owner completing their own prospect's referencing no longer
    // notifies themselves, where today they do.
  }

  return data
}
